/**
 * Manage, Groups: the list (plan docs/plans/lms-plan.md 8.2, 5.3, 18.20;
 * WP-43; R6, R19).
 *
 * The network's groups by name (GET /admin/groups, 50 a page), filtered by
 * kind (requirement profiles, saved audiences) and, on request, including the
 * deactivated ones. Each row shows the kind, the active member count and, for
 * a profile, the number of courses it requires (18.20), and opens the group.
 * "New group" opens NewGroupModal.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Plus, UsersRound } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import { chipCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { listGroups } from '../../../api/groups';
import type { GroupKind, GroupListItem } from '../../../api/groups';
import {
  ErrorNote,
  metaCls,
  Pill,
  rowCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../../components/pickers/shared';
import NewGroupModal from './NewGroupModal';
import { groupHref } from './reconcile';

const PAGE = 50;

const KIND_FILTERS: Array<{ value: GroupKind | ''; label: string }> = [
  { value: '', label: 'All groups' },
  { value: 'profile', label: 'Requirement profiles' },
  { value: 'adhoc', label: 'Saved audiences' },
];

export function KindPill({ kind }: { kind: GroupKind }) {
  return <Pill tone={kind === 'profile' ? 'primary' : 'gray'}>{kind === 'profile' ? 'Profile' : 'Saved audience'}</Pill>;
}

export function GroupList() {
  const navigate = useNavigate();
  const [kind, setKind] = useState<GroupKind | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [groups, setGroups] = useState<GroupListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const request = useRef(0);

  const load = useCallback(
    (offset: number) => {
      const id = ++request.current;
      setLoading(true);
      setError(null);
      listGroups({ kind: kind || null, includeInactive, limit: PAGE, offset })
        .then((page) => {
          if (id !== request.current) return;
          const items = Array.isArray(page.items) ? page.items : [];
          setGroups((prev) => (offset === 0 ? items : [...prev, ...items]));
          setHasMore(Boolean(page.page?.hasMore));
        })
        .catch((err) => {
          if (id !== request.current) return;
          setError(apiErrorMessage(err, 'Could not load the groups.'));
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    },
    [kind, includeInactive]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <div className="space-y-4" data-testid="groups-list">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter groups">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              aria-pressed={kind === f.value}
              className={chipCls(kind === f.value)}
              onClick={() => setKind(f.value)}
              data-testid={`groups-filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={includeInactive}
            className={chipCls(includeInactive)}
            onClick={() => setIncludeInactive((v) => !v)}
            data-testid="groups-filter-inactive"
          >
            Show deactivated
          </button>
        </div>
        <button type="button" className={primaryBtnCls} onClick={() => setCreating(true)} data-testid="new-group">
          <Plus className="h-4 w-4" />
          New group
        </button>
      </div>

      {error && (
        <ErrorNote>
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => load(0)}>
            Retry
          </button>
        </ErrorNote>
      )}

      {!error && !loading && groups.length === 0 ? (
        <EmptyState
          icon={UsersRound}
          variant="card"
          title="No groups yet"
          hint="A requirement profile assigns its courses to its members on its own; a saved audience is a list of people to send courses to. The catalog install adds four suggested profiles with no members."
          action={
            <button type="button" className={primaryBtnCls} onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New group
            </button>
          }
        />
      ) : (
        groups.length > 0 && (
          <div className={tableCardCls}>
            <table className={tableCls}>
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>
                    Group
                  </th>
                  <th scope="col" className={thCls}>
                    Kind
                  </th>
                  <th scope="col" className={`${thCls} text-right`}>
                    Members
                  </th>
                  <th scope="col" className={`${thCls} text-right`}>
                    Required courses
                  </th>
                  <th scope="col" className={thCls}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {groups.map((g) => (
                  <tr key={g.id} className={rowCls} data-testid={`group-row-${g.id}`}>
                    <td className={tdCls}>
                      <Link
                        to={groupHref(g.id)}
                        className="font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                      >
                        {g.name}
                      </Link>
                      {g.description && <p className={`${metaCls} mt-0.5 line-clamp-1`}>{g.description}</p>}
                    </td>
                    <td className={tdCls}>
                      <KindPill kind={g.kind} />
                    </td>
                    <td className={`${tdCls} text-right tabular-nums`}>{(g.member_count ?? 0).toLocaleString()}</td>
                    <td className={`${tdCls} text-right tabular-nums`}>
                      {g.kind === 'profile' ? (g.requirement_count ?? 0).toLocaleString() : <span className={metaCls}>Not a profile</span>}
                    </td>
                    <td className={tdCls}>
                      {g.is_active ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Deactivated</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <div className="flex items-center gap-2">
        {loading && <Loader2 aria-label="Loading groups" className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />}
        {hasMore && !loading && (
          <button type="button" className={secondaryBtnCls} onClick={() => load(groups.length)} data-testid="groups-more">
            Show more groups
          </button>
        )}
      </div>

      <NewGroupModal isOpen={creating} onClose={() => setCreating(false)} onOpenGroup={(id) => navigate(groupHref(id))} />
    </div>
  );
}

export default GroupList;
